import { useTranslation } from "react-i18next";

import { VatSalesTreatment } from "./VehicleVatStatusCell";

export type VatStatusFilter = "all" | "vat" | "noVat" | "margin" | "standard";

/**
 * Search-body fragment for a VAT filter selection.
 * `keyPrefix` lets the payments table send the vehicle-scoped names its filter DTO expects
 * (its own ApplyVAT is the lead mirror, so the field names there are deliberately different).
 */
export function vatStatusSearchBody(
  value: VatStatusFilter,
  keyPrefix: "" | "vehicle" = ""
): Record<string, unknown> {
  const applyVatKey = keyPrefix ? `${keyPrefix}ApplyVAT` : "applyVAT";
  const treatmentKey = keyPrefix ? `${keyPrefix}VatSalesTreatment` : "vatSalesTreatment";

  switch (value) {
    case "vat":
      return { [applyVatKey]: true };
    case "noVat":
      return { [applyVatKey]: false };
    case "margin":
      return { [treatmentKey]: VatSalesTreatment.MarginScheme };
    case "standard":
      return { [treatmentKey]: VatSalesTreatment.Standard };
    case "all":
    default:
      return {};
  }
}

type VatStatusFilterSelectProps = {
  value: VatStatusFilter;
  onChange: (value: VatStatusFilter) => void;
  disabled?: boolean;
};

export default function VatStatusFilterSelect({
  value,
  onChange,
  disabled,
}: VatStatusFilterSelectProps) {
  const { t } = useTranslation("vehicles");

  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as VatStatusFilter)}
      className="h-10 text-sm border-gray-300 rounded-lg focus:border-primary-500 focus:ring-primary-500"
      aria-label={t("VAT status")}
    >
      <option value="all">{t("All VAT statuses")}</option>
      <option value="vat">{t("VAT on purchase")}</option>
      <option value="noVat">{t("No VAT on purchase")}</option>
      <option value="margin">{t("Sales: margin scheme")}</option>
      <option value="standard">{t("Sales: standard")}</option>
    </select>
  );
}
